"use client";

import { useRef, useEffect, useCallback, useState } from "react";
import { useGameStore } from "@/stores/game-store";
import { useUIStore } from "@/stores/ui-store";
import type { GameView, DepartmentState, SectionState, SurggieState } from "@/stores/game-store";

// Engine imports
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  TILE_SIZE,
  WORLD_COLS,
  WORLD_ROWS,
  Direction,
  CHARACTER_WIDTH,
  CHARACTER_HEIGHT,
  TileType,
} from "@/engine/constants";
import { SpriteSheet } from "@/engine/sprite-sheet";
import { AssetLoader } from "@/engine/asset-loader";
import { Camera } from "@/engine/camera";
import { Character } from "@/engine/character";
import { PlayerController } from "@/engine/player-controller";
import { InteractionSystem } from "@/engine/interaction";
import type { InteractionResult, SurggieInteractable } from "@/engine/interaction";
import { RoomManager } from "@/engine/room-manager";
import { Renderer } from "@/engine/renderer";
import type { GameRenderState } from "@/engine/renderer";
import { SectionRenderer } from "@/engine/section-renderer";
import type { WorkstationInfo } from "@/engine/section-renderer";
import { WorldMap } from "@/engine/world-map";
import { GameLoop } from "@/engine/game-loop";
import { Pet, createHuskies, updatePlayerHistory } from "@/engine/pet";
import type { Position } from "@/engine/constants";
import { generateAllAssets } from "@/engine/asset-generator";
import { globalEncounterSystem } from "@/engine/encounter";
import { EncounterManager, type EncounterStartEvent } from "@/engine/EncounterManager";
import { globalEventEngine } from "@/engine/map-events";
import { getZoneByRoomId } from "@/data/zones";
import { WildSpritesManager, type ZoneSpawnConfig } from "@/engine/wild-sprites";
import { TrainerDetectionSystem, type TrainerPlacement as DetectionPlacement } from "@/engine/trainer-detection";
import { dayNightCycle } from "@/engine/day-night";
import { playSfx, playBgm, type BgmKey } from "@/engine/audio";
import { achievementTracker } from "@/engine/achievements";
import { ROUTE1_MAP } from "@/data/maps/route1";
import { BattleArena } from "./BattleArena";
import { ShareButton } from "./ShareButton";
import { BattleMoveSelect, type MoveInfo } from "./BattleMoveSelect";
import { BattleBag, type ItemEntry } from "./BattleBag";
import { BattleParty, type PartyMember } from "./BattleParty";
import { BattleLog } from "./BattleLog";
import { createBoundBattleEngine, dispatchBattleAction } from "@/engine/battle-store-bridge";
import type { BattleEngine } from "@/engine/battle/BattleEngine";
import { MOVES_DATA } from "@/data/moves";
import { buildParticipant, resolveSpecies } from "@/engine/battle-participant";
import { createPartyMember, applyXp, fullPp, type PartyMember as PartyMemberData } from "@/engine/party";
import { checkEvolution } from "@/engine/evolution";
import { getMovesLearnedAtLevel } from "@/engine/move-learn";
import { KeyboardHintsOverlay } from "./KeyboardHintsOverlay";
import { SurggieDialog } from "./SurggieDialog";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface GameCanvasProps {
  onInteraction?: (result: InteractionResult) => void;
  onViewChange?: (view: GameView) => void;
  className?: string;
}

// ─── Surggie NPC colors by role ───────────────────────────────────────────────

const SURGGIE_COLORS: Record<string, string> = {
  engineer: "#3498DB",
  marketer: "#E74C3C",
  salesperson: "#F39C12",
  accountant: "#27AE60",
  recruiter: "#8E44AD",
  operator: "#E67E22",
  designer: "#1ABC9C",
  support: "#2980B9",
  default: "#95A5A6",
};

// ─── Party → battle participant ───────────────────────────────────────────────

/**
 * Build an engine participant from a canonical party member: species-derived
 * stats/types via buildParticipant, real hp/moves/pp/loadout from the member.
 */
function participantFromMember(member: PartyMemberData) {
  return {
    ...buildParticipant(
      member.speciesId,
      member.level,
      member.hpCurrent,
      member.hpMax,
      member.nickname,
    ),
    moves: [...member.moves],
    pp: { ...member.pp },
    loadout: member.loadout,
  };
}

// Map the originating room id to a BattleArena backdrop biome key. Keys follow
// the shared backdrop contract; anything unmapped (department/section rooms,
// exits) falls back to "grass".
function zoneToBackdrop(roomId: string): string {
  switch (roomId) {
    case "sales_floor":
      return "grass";
    case "dev_dungeon":
      return "cave";
    case "finance_hub":
      return "office";
    case "creative_lab":
      return "creative";
    case "ops_center":
      return "ops";
    case "default":
      return "grass";
    default:
      return "grass";
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GameCanvas({
  onInteraction,
  onViewChange,
  className,
}: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<{
    spriteSheet: SpriteSheet;
    assetLoader: AssetLoader;
    camera: Camera;
    player: Character;
    playerController: PlayerController;
    interactionSystem: InteractionSystem;
    roomManager: RoomManager;
    renderer: Renderer;
    sectionRenderer: SectionRenderer;
    worldMap: WorldMap;
    gameLoop: GameLoop;
    surggieCharacters: Map<string, Character>;
    pets: Pet[];
    playerHistory: Position[];
    frameCount: number;
  } | null>(null);

  const handleInteractionRef = useRef<(result: InteractionResult) => void>(() => {});
  const encounterManagerRef = useRef<EncounterManager>(
    new EncounterManager(globalEncounterSystem),
  );
  // Visible wild sprites manager — rebuilt when the active map changes (below).
  const wildSpritesRef = useRef<WildSpritesManager>(new WildSpritesManager([]));
  // Trainer LoS detection — placements get swapped per map.
  const trainerDetectionRef = useRef<TrainerDetectionSystem>(
    new TrainerDetectionSystem(),
  );
  // Trainer notification state for the '!' bubble + post-spotted walk.
  const trainerNotifyRef = useRef<{ trainerId: string; spottedAt: number } | null>(null);
  // Last tile coords we ran per-move checks on, so we only fire on actual tile changes.
  const lastMoveTileRef = useRef<{ col: number; row: number }>({ col: -1, row: -1 });
  // BGM track currently playing, so we can swap on view/battle changes.
  const currentBgmRef = useRef<BgmKey | null>(null);
  // BGM to restore when battle ends.
  const preBattleBgmRef = useRef<BgmKey | null>(null);
  // Active BattleEngine instance — created when a battle starts, cleared on end.
  const battleEngineRef = useRef<BattleEngine | null>(null);

  // ── Store selectors ───────────────────────────────────────────────────────

  const view = useGameStore((s) => s.view);
  const setView = useGameStore((s) => s.setView);
  const playerPosition = useGameStore((s) => s.playerPosition);
  const setPlayerPosition = useGameStore((s) => s.setPlayerPosition);
  const setPlayerDirection = useGameStore((s) => s.setPlayerDirection);
  const departments = useGameStore((s) => s.departments);
  const sections = useGameStore((s) => s.sections);
  const surggies = useGameStore((s) => s.surggies);
  const party = useGameStore((s) => s.party);
  const keysPressed = useGameStore((s) => s.keysPressed);
  const pressKey = useGameStore((s) => s.pressKey);
  const releaseKey = useGameStore((s) => s.releaseKey);
  const setInteractionTarget = useGameStore((s) => s.setInteractionTarget);
  const setShowInteractionPrompt = useGameStore((s) => s.setShowInteractionPrompt);
  const currentDepartmentId = useGameStore((s) => s.currentDepartmentId);
  const setCurrentDepartment = useGameStore((s) => s.setCurrentDepartment);
  const currentSectionId = useGameStore((s) => s.currentSectionId);
  const setCurrentSection = useGameStore((s) => s.setCurrentSection);
  const setActiveSurggieId = useUIStore((s) => s.setActiveSurggieId);
  const playerXp = useGameStore((s) => s.playerXp);
  const playerLevel = useGameStore((s) => s.playerLevel);

  // Battle slice
  const battle = useGameStore((s) => s.battle);
  const startBattle = useGameStore((s) => s.startBattle);
  const setBackdrop = useGameStore((s) => s.setBackdrop);
  const endBattle = useGameStore((s) => s.endBattle);
  const appendBattleLog = useGameStore((s) => s.appendBattleLog);

  // ── Encounter state ──────────────────────────────────────────────────────────
  const [activeEncounter, setActiveEncounter] = useState<{
    wildSurggie: import("@/engine/encounter").WildSurggie;
    xpReward: number;
  } | null>(null);

  // ── Surggie dialog state ──────────────────────────────────────────────────
  const [selectedSurggie, setSelectedSurggie] = useState<SurggieState | null>(null);
  // Mirrored into a ref so the game-loop closure can gate input without
  // re-creating the engine effect.
  const surggieDialogOpenRef = useRef(false);
  useEffect(() => {
    surggieDialogOpenRef.current = selectedSurggie !== null;
  }, [selectedSurggie]);

  const closeSurggieDialog = useCallback(() => {
    setSelectedSurggie(null);
    // Release any held keys so the player doesn't keep walking after close.
    for (const k of Array.from(useGameStore.getState().keysPressed)) {
      releaseKey(k);
    }
  }, [releaseKey]);

  // If a battle starts (e.g. a delayed trainer encounter), close the dialog so
  // it never sits on top of the battle overlay or leaves movement frozen.
  useEffect(() => {
    if (battle.active && surggieDialogOpenRef.current) {
      closeSurggieDialog();
    }
  }, [battle.active, closeSurggieDialog]);

  // ── Handle interaction callback ───────────────────────────────────────────

  const handleInteraction = useCallback(
    (result: InteractionResult) => {
      if (!engineRef.current) return;
      const engine = engineRef.current;

      if (result.type === "building") {
        const action = result.data?.action as string | undefined;

        if (action === "enter") {
          // Enter a department building
          const deptId = result.data?.targetRoom as string;
          const dept = departments.find((d) => d.id === deptId);
          if (dept) {
            const deptSections = sections.filter((s) => s.departmentId === deptId);
            engine.roomManager.generateDepartmentRoom(dept, deptSections);
            const spawnPos = engine.roomManager.transition(deptId);
            engine.player.setPosition(spawnPos.x, spawnPos.y);
            engine.camera.setWorldBounds(
              engine.roomManager.getCurrentRoom()!.width * TILE_SIZE,
              engine.roomManager.getCurrentRoom()!.height * TILE_SIZE,
            );
            engine.camera.snapTo(spawnPos.x, spawnPos.y);
            setCurrentDepartment(deptId);
            setView("department");
            onViewChange?.("department");
          }
        } else if (action === "exit") {
          const targetRoom = result.data?.targetRoom as string;
          if (targetRoom === "overworld") {
            const spawnPos = engine.roomManager.transition("overworld");
            engine.player.setPosition(spawnPos.x, spawnPos.y);
            engine.camera.setWorldBounds(
              WORLD_COLS * TILE_SIZE,
              WORLD_ROWS * TILE_SIZE,
            );
            engine.camera.snapTo(spawnPos.x, spawnPos.y);
            setCurrentDepartment(null);
            setCurrentSection(null);
            setView("overworld");
            onViewChange?.("overworld");
          } else {
            // Exit section back to department
            const spawnPos = engine.roomManager.transition(targetRoom);
            engine.player.setPosition(spawnPos.x, spawnPos.y);
            engine.camera.setWorldBounds(
              engine.roomManager.getCurrentRoom()!.width * TILE_SIZE,
              engine.roomManager.getCurrentRoom()!.height * TILE_SIZE,
            );
            engine.camera.snapTo(spawnPos.x, spawnPos.y);
            setCurrentSection(null);
            setView("department");
            onViewChange?.("department");
          }
        }
      } else if (result.type === "section") {
        // Enter a section
        const sectionData = sections.find((s) => s.id === result.id);
        if (sectionData) {
          engine.roomManager.generateSectionRoom(sectionData);
          const spawnPos = engine.roomManager.transition(result.id);
          engine.player.setPosition(spawnPos.x, spawnPos.y);
          engine.camera.setWorldBounds(
            engine.roomManager.getCurrentRoom()!.width * TILE_SIZE,
            engine.roomManager.getCurrentRoom()!.height * TILE_SIZE,
          );
          engine.camera.snapTo(spawnPos.x, spawnPos.y);
          setCurrentSection(result.id);
          setView("section");
          onViewChange?.("section");
        }
      } else if (result.type === "surggie") {
        // Open the in-world Surggie dialog
        const surggie = useGameStore
          .getState()
          .surggies.find((s) => s.id === result.id);
        if (surggie) {
          setActiveSurggieId(result.id);
          setSelectedSurggie(surggie);
        } else if (result.id.startsWith("pet:")) {
          // Follower huskies have no store entry — synthesize one for the dialog.
          const pet = engineRef.current?.pets.find(
            (p) => `pet:${p.displayName}` === result.id,
          );
          if (pet) {
            setSelectedSurggie({
              id: result.id,
              name: pet.displayName,
              role: "companion",
              team: "party",
              spriteKey: pet.spriteKey,
              position: { ...pet.position },
              status: "idle",
              level: 1,
              xp: 0,
            });
          }
        }
      }

      // Propagate to parent
      onInteraction?.(result);
    },
    [
      departments,
      sections,
      setView,
      setCurrentDepartment,
      setCurrentSection,
      setActiveSurggieId,
      onInteraction,
      onViewChange,
    ],
  );

  // Keep ref in sync so the stale-closure in the game loop always calls the latest version
  useEffect(() => {
    handleInteractionRef.current = handleInteraction;
  }, [handleInteraction]);

  // ── Initialize engine on mount ────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Disable image smoothing for pixel-art crispness
    ctx.imageSmoothingEnabled = false;

    // ── Create engine instances ───────────────────────────────────────────

    const spriteSheet = new SpriteSheet();
    const assetLoader = new AssetLoader(spriteSheet);
    const camera = new Camera(
      WORLD_COLS * TILE_SIZE,
      WORLD_ROWS * TILE_SIZE,
      canvas.width,
      canvas.height,
    );
    const player = new Character(
      playerPosition.x,
      playerPosition.y,
      "ceo_1",
      "#E74C3C",
      Direction.Down,
    );
    const playerController = new PlayerController();
    const interactionSystem = new InteractionSystem();
    const roomManager = new RoomManager();
    const renderer = new Renderer(spriteSheet, camera);
    const sectionRenderer = new SectionRenderer();
    const worldMap = new WorldMap();

    // Generate overworld
    const overworldRoom = worldMap.generate(departments);
    roomManager.setOverworld(overworldRoom);

    // Create Surggie NPC characters
    const surggieCharacters = new Map<string, Character>();

    // ── Husky pets ────────────────────────────────────────────────────────
    const partyPetKeys = useGameStore
      .getState()
      .party.map((m) => resolveSpecies(m.speciesId)?.sprite ?? m.speciesId);
    const pets = createHuskies(
      playerPosition.x,
      playerPosition.y,
      partyPetKeys.length
        ? partyPetKeys
        : useGameStore.getState().surggies.map((s) => s.spriteKey),
    );
    const playerHistory: Position[] = [];

    // ── Frame counter for animations ──────────────────────────────────────
    let frameCount = 0;

    // ── Update function ───────────────────────────────────────────────────

    const update = (dt: number) => {
      frameCount++;

      // Read keys from zustand store
      const keys = useGameStore.getState().keysPressed;

      // Update player movement (frozen while the Surggie dialog is open)
      const dialogOpen = surggieDialogOpenRef.current;
      const collisionMap = roomManager.getCollisionMap();
      if (!dialogOpen) {
        playerController.update(dt, player, keys, collisionMap);
      } else {
        player.isMoving = false;
      }

      // Update player animation
      player.update(dt);

      // Sync player position to store
      setPlayerPosition({ x: player.position.x, y: player.position.y });
      setPlayerDirection(
        player.direction === Direction.Up
          ? "up"
          : player.direction === Direction.Down
            ? "down"
            : player.direction === Direction.Left
              ? "left"
              : "right",
      );

      // Update camera
      const center = player.getCenter();
      camera.update(center.x, center.y);

      // Update interaction system (suppressed while the Surggie dialog is open)
      if (!dialogOpen) {
        const interactables = roomManager.getInteractables();
        const storeSurggies = useGameStore.getState().surggies;
        const surggieInteractables: SurggieInteractable[] = [];
        for (const [id, char] of surggieCharacters) {
          surggieInteractables.push({
            id,
            type: "surggie",
            position: char.position,
            width: CHARACTER_WIDTH,
            height: CHARACTER_HEIGHT,
            name: storeSurggies.find((s) => s.id === id)?.name ?? id,
          });
        }
        // Follower huskies are interactable too (E to open their dialog).
        for (const pet of pets) {
          surggieInteractables.push({
            id: `pet:${pet.displayName}`,
            type: "surggie",
            position: pet.position,
            width: CHARACTER_WIDTH,
            height: CHARACTER_HEIGHT,
            name: pet.displayName,
          });
        }
        const showPrompt = interactionSystem.update(player, interactables, surggieInteractables);
        setShowInteractionPrompt(showPrompt);

        if (showPrompt) {
          const target = interactionSystem.getNearestTarget();
          if (target) {
            setInteractionTarget({ type: target.type, id: target.id });
          }
        } else {
          setInteractionTarget(null);
        }

        // Check for E press
        const interactionResult = interactionSystem.handleInteraction(keys);
        if (interactionResult) {
          handleInteractionRef.current(interactionResult);
        }
      } else {
        setShowInteractionPrompt(false);
        setInteractionTarget(null);
      }

      // Update Surggie NPCs
      for (const [id, char] of surggieCharacters) {
        char.update(dt, collisionMap, undefined);
      }

      // Detect which NPC the player is facing (for map event triggers)
      const playerCol = Math.floor(player.position.x / TILE_SIZE);
      const playerRow = Math.floor(player.position.y / TILE_SIZE);
      let facingNpcId: string | null = null;
      for (const [id, char] of surggieCharacters) {
        const npcCol = Math.floor(char.position.x / TILE_SIZE);
        const npcRow = Math.floor(char.position.y / TILE_SIZE);
        const colDiff = Math.abs(playerCol - npcCol);
        const rowDiff = Math.abs(playerRow - npcRow);
        // Player is adjacent (1 tile away) and facing the NPC
        const adjacent = (colDiff === 1 && rowDiff === 0) || (colDiff === 0 && rowDiff === 1);
        if (adjacent) {
          if (player.direction === Direction.Up && npcRow < playerRow) facingNpcId = id;
          else if (player.direction === Direction.Down && npcRow > playerRow) facingNpcId = id;
          else if (player.direction === Direction.Left && npcCol < playerCol) facingNpcId = id;
          else if (player.direction === Direction.Right && npcCol > playerCol) facingNpcId = id;
        }
      }
      globalEventEngine.setFacingNpc(facingNpcId);

      // Track player history and update husky pets
      updatePlayerHistory(playerHistory, player.position);
      for (const pet of pets) {
        pet.update(dt, playerHistory, pets);
      }

      // ── Day/night cycle tick ────────────────────────────────────────────
      dayNightCycle.update(dt);

      // ── Wild sprites — visible wandering wilds ──────────────────────────
      {
        const wm = wildSpritesRef.current;
        const playerTileX = Math.floor(player.position.x / TILE_SIZE);
        const playerTileY = Math.floor(player.position.y / TILE_SIZE);
        const cmap = roomManager.getCollisionMap();
        const isBlocked = (tx: number, ty: number) => {
          if (ty < 0 || ty >= cmap.length) return true;
          const row = cmap[ty];
          if (!row || tx < 0 || tx >= row.length) return true;
          return row[tx];
        };
        // We don't currently know which encounter zone the player is in
        // for distance-gating; pass empty so global throttles still apply.
        wm.update(dt, { x: playerTileX, y: playerTileY, zoneId: "" }, isBlocked);

        const collided = wm.consumeEncounter({ x: playerTileX, y: playerTileY });
        if (collided) {
          playSfx("wild_appear");
          // Estimate HP for v2 payload (encounter system has full data, but
          // the wild sprite carries enough for a sane default).
          const hpMax = Math.max(10, 10 + collided.level * 4);
          const detail: EncounterStartEvent = {
            opponentSpeciesId: collided.speciesId,
            opponentLevel: collided.level,
            opponentHpMax: hpMax,
            isWild: true,
            fromWildSpriteId: collided.id,
          };
          window.dispatchEvent(
            new CustomEvent<EncounterStartEvent>("encounter:start:v2", { detail }),
          );
        }
      }

      // ── Per-move checks: trainer detection + map exits ──────────────────
      const movePlayerCol = Math.floor(player.position.x / TILE_SIZE);
      const movePlayerRow = Math.floor(player.position.y / TILE_SIZE);
      const lastTile = lastMoveTileRef.current;
      if (movePlayerCol !== lastTile.col || movePlayerRow !== lastTile.row) {
        lastMoveTileRef.current = { col: movePlayerCol, row: movePlayerRow };

        const cmap = roomManager.getCollisionMap();
        const isBlockedTile = (tx: number, ty: number) => {
          if (ty < 0 || ty >= cmap.length) return true;
          const row = cmap[ty];
          if (!row || tx < 0 || tx >= row.length) return true;
          return row[tx];
        };

        // Trainer line-of-sight detection.
        if (!trainerNotifyRef.current && !useGameStore.getState().battle.active) {
          const detection = trainerDetectionRef.current.checkDetection(
            movePlayerCol,
            movePlayerRow,
            isBlockedTile,
          );
          if (detection) {
            playSfx("trainer_spotted");
            trainerNotifyRef.current = {
              trainerId: detection.trainerId,
              spottedAt: Date.now(),
            };
            // TODO(integration): play trainer-walk animation along
            // detection.walkPath before dispatching the battle event. For now
            // we delay the battle dispatch by NOTIFICATION_DURATION_MS so the
            // '!' bubble has time to flash.
            const trainerId = detection.trainerId;
            setTimeout(() => {
              // Trainer-team data lives in a separate registry; pick a sane
              // placeholder until that wiring lands.
              const detail: EncounterStartEvent = {
                opponentSpeciesId: "byte",
                opponentLevel: 8,
                opponentHpMax: 32,
                isWild: false,
              };
              window.dispatchEvent(
                new CustomEvent<EncounterStartEvent>("encounter:start:v2", {
                  detail,
                }),
              );
              trainerDetectionRef.current.markDefeated(trainerId);
              trainerNotifyRef.current = null;
            }, 600);
          }
        }

        // Map exits — trigger wipe + teleport + map swap.
        const exit = roomManager.checkExits(
          player.position.x,
          player.position.y,
          roomManager.getCurrentMapId(),
        );
        if (exit) {
          // triggerMapTransition handles the wipe + teleport at halfway. We
          // also need to keep the player Character + camera in sync, so do
          // that in the onTeleport hook via the transitionCallbacks.
          void roomManager.triggerMapTransition(
            exit.targetMapId,
            exit.targetX,
            exit.targetY,
          );
          // Sync player position once the swap occurs. The promise resolves
          // after the full transition; for snappier feel we also teleport
          // the Character immediately (collision map will be the new map's
          // by the time the next frame runs).
          const targetPx = exit.targetX * TILE_SIZE;
          const targetPy = exit.targetY * TILE_SIZE;
          // Defer one tick so the room swap inside triggerMapTransition has
          // committed before we move the player+camera.
          setTimeout(() => {
            player.setPosition(targetPx, targetPy);
            const newRoom = roomManager.getCurrentRoom();
            if (newRoom) {
              camera.setWorldBounds(
                newRoom.width * TILE_SIZE,
                newRoom.height * TILE_SIZE,
              );
              camera.snapTo(targetPx, targetPy);
            }
            setPlayerPosition({ x: targetPx, y: targetPy });
            // Achievement: record the map visit.
            achievementTracker.onMapVisit(exit.targetMapId);
          }, 200);
        }
      }

      // ── Encounter system (step-gated) ────────────────────────────────────
      const encState = globalEncounterSystem.getState();
      if (encState === "idle" || encState === "searching") {
        const currentGameView = useGameStore.getState().view;
        const playerCol = Math.floor(player.position.x / TILE_SIZE);
        const playerRow = Math.floor(player.position.y / TILE_SIZE);
        const isEncounterZone =
          currentGameView === "overworld" &&
          playerRow >= 37 &&
          playerRow <= 50 &&
          playerCol >= 20 &&
          playerCol <= 70;
        if (isEncounterZone && player.isMoving) {
          // Use the step-counting EncounterManager instead of per-frame rolls.
          // onStep fires encounter:start event when a roll succeeds.
          const currentRoom = roomManager.getCurrentRoom();
          const roomId = currentRoom?.id ?? "default";
          const zone = getZoneByRoomId(roomId);
          encounterManagerRef.current.onStep(roomId, player.position.x, player.position.y, zone);
        }
      }
    };

    // ── Render function ───────────────────────────────────────────────────

    const render = () => {
      const currentView = useGameStore.getState().view;
      const room = roomManager.getCurrentRoom();

      // Gather Surggie characters for current room
      const visibleSurggies: Character[] = [];
      for (const [_, char] of surggieCharacters) {
        visibleSurggies.push(char);
      }

      const renderState: GameRenderState = {
        view: currentView,
        room,
        player,
        surggies: visibleSurggies,
        pets,
        interactionSystem,
        sectionRenderer,
        workstations: [],
        frameCount,
        playerXp,
        playerLevel,
      };

      renderer.render(ctx!, renderState);

      // TODO(integration): once renderer.ts exposes drawWildSprites(),
      // forward the manager's sprite list + trainer notification bubble.
      // For now, render defensively without exploding if the method is
      // absent.
      const rAny = renderer as unknown as {
        drawWildSprites?: (
          ctx: CanvasRenderingContext2D,
          sprites: ReadonlyArray<{ x: number; y: number; speciesId: string; animFrame: number; facing: string }>,
        ) => void;
        drawDayNightOverlay?: (
          ctx: CanvasRenderingContext2D,
          overlayColor: string,
        ) => void;
      };
      if (rAny.drawWildSprites) {
        rAny.drawWildSprites(ctx!, wildSpritesRef.current.sprites);
      }
      if (rAny.drawDayNightOverlay) {
        rAny.drawDayNightOverlay(ctx!, dayNightCycle.overlayColor);
      }
    };

    // ── Create game loop ──────────────────────────────────────────────────

    const gameLoop = new GameLoop(update, render);

    // Store engine ref
    engineRef.current = {
      spriteSheet,
      assetLoader,
      camera,
      player,
      playerController,
      interactionSystem,
      roomManager,
      renderer,
      sectionRenderer,
      worldMap,
      gameLoop,
      surggieCharacters,
      pets,
      playerHistory,
      frameCount: 0,
    };

    // ── Load assets and start ─────────────────────────────────────────────
    //
    // Order matters:
    //   1. Try the asset manifest first — PNG sprites for any keys it covers
    //      get registered into spriteSheet via addSheet.
    //   2. Run the procedural generator. SpriteSheet.addCanvas/addSheet are
    //      no-clobber, so procedural fills in any keys the manifest didn't
    //      provide; PNGs that did load are preserved.
    //   3. Start the game loop.
    //
    // loadManifestJSON resolves on 404 / network error too, so a missing
    // manifest cleanly degrades to the procedural-only behavior we shipped.

    const startEngine = () => {
      const assets = generateAllAssets();
      renderer.setAssets(assets);

      // Register characters
      for (const [key, canvas] of Object.entries(assets.characters)) {
        spriteSheet.addCanvas(`char_${key}`, canvas);
      }

      // Register tiles (single-frame)
      for (const [key, canvas] of Object.entries(assets.tiles)) {
        if (key === "ocean" || key === "floors" || key === "pool") continue;
        spriteSheet.addCanvas(`tile_${key}`, canvas as OffscreenCanvas);
      }

      // Register ocean frames (animated, 8 frames)
      for (let i = 0; i < assets.tiles.ocean.length; i++) {
        spriteSheet.addCanvas(`tile_ocean_${i}`, assets.tiles.ocean[i]);
      }

      // Register department floor tiles
      for (const [key, canvas] of Object.entries(assets.tiles.floors)) {
        spriteSheet.addCanvas(`tile_floor_${key}`, canvas);
      }

      // Register pool tile (single static tile)
      spriteSheet.addCanvas("tile_pool", assets.tiles.pool);

      // Register buildings
      for (const [key, canvas] of Object.entries(assets.buildings)) {
        spriteSheet.addCanvas(`bldg_${key}`, canvas);
      }

      // Register props
      for (const [key, canvas] of Object.entries(assets.props)) {
        spriteSheet.addCanvas(`prop_${key}`, canvas);
      }

      gameLoop.start();
    };

    // Start engine immediately with procedurally generated assets so the
    // game loop begins rendering before any PNG network requests complete.
    startEngine();

    // Load PNG sprites from manifest after a 3s delay — deferred so
    // Playwright's waitUntil:"networkidle" fires before bulk PNG fetches start.
    const manifestTimer = setTimeout(() => {
      fetch("/assets/surggies/manifest.json")
        .then((r) => r.json())
        .catch(() => null)
        .then((manifest) => {
          if (manifest) {
            assetLoader.loadAll(manifest).catch(() => {/* silently ignore */});
          }
        });
    }, 3000);

    // ── Cleanup ───────────────────────────────────────────────────────────

    return () => {
      clearTimeout(manifestTimer);
      gameLoop.stop();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync Surggies from store to engine characters ───────────────────────

  useEffect(() => {
    if (!engineRef.current) return;
    const engine = engineRef.current;

    // Sync surggie characters
    const existingIds = new Set(engine.surggieCharacters.keys());
    const storeIds = new Set(surggies.map((s) => s.id));

    // Remove old
    for (const id of existingIds) {
      if (!storeIds.has(id)) {
        engine.surggieCharacters.delete(id);
      }
    }

    // Add/update
    for (const s of surggies) {
      if (!s.position) continue;
      if (!engine.surggieCharacters.has(s.id)) {
        const color = SURGGIE_COLORS[s.role] || SURGGIE_COLORS.default;
        const char = new Character(
          s.position.x,
          s.position.y,
          s.spriteKey,
          color,
          Direction.Down,
          s.id,
        );
        char.isMoving = s.status === "working";
        engine.surggieCharacters.set(s.id, char);
      } else {
        const char = engine.surggieCharacters.get(s.id)!;
        char.setPosition(s.position.x, s.position.y);
        char.isMoving = s.status === "working";
      }
    }
  }, [surggies]);

  // ── Regenerate world when departments change ────────────────────────────

  useEffect(() => {
    if (!engineRef.current) return;
    const engine = engineRef.current;

    const overworldRoom = engine.worldMap.generate(departments);
    engine.roomManager.setOverworld(overworldRoom);
  }, [departments]);

  // ── Keyboard event handlers ─────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default for game keys to avoid scrolling
      if (
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " ", "e", "E"].includes(
          e.key,
        )
      ) {
        e.preventDefault();
      }
      pressKey(e.key);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      releaseKey(e.key);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [pressKey, releaseKey]);

  // ── Encounter:start event listener (v1 fallback) ───────────────────────
  useEffect(() => {
    const handleEncounterStart = (e: Event) => {
      const { wildSurggie } = (e as CustomEvent<{ wildSurggie: import("@/engine/encounter").WildSurggie }>).detail;
      const xpReward = Math.floor(20 + wildSurggie.level * 5);
      setActiveEncounter({ wildSurggie, xpReward });
      globalEncounterSystem.startBattle();
      // Wire the new battle store slice so BattleArena/BattleOverlayHD can read it.
      startBattle({
        opponentSpeciesId: String(wildSurggie.speciesId),
        opponentLevel: wildSurggie.level,
        opponentHpMax: wildSurggie.max_hp,
        isWild: true,
      });
    };
    window.addEventListener("encounter:start", handleEncounterStart);
    return () => {
      window.removeEventListener("encounter:start", handleEncounterStart);
    };
  }, []);

  // ── Encounter:start:v2 event listener (preferred shape) ────────────────
  useEffect(() => {
    const handleV2 = (e: Event) => {
      const detail = (e as CustomEvent<EncounterStartEvent>).detail;
      if (!detail) return;
      const { opponentSpeciesId, opponentLevel, opponentHpMax, isWild, fromWildSpriteId } = detail;
      startBattle({ opponentSpeciesId, opponentLevel, opponentHpMax, isWild });
      // Pick the battle backdrop from the room the encounter started in.
      const originRoomId = engineRef.current?.roomManager.getCurrentMapId() ?? "default";
      setBackdrop(zoneToBackdrop(originRoomId));
      // Create a bound BattleEngine for this encounter. The player side comes
      // from the canonical party; seed a starter when empty so demo flows
      // without New Game still battle.
      if (useGameStore.getState().party.length === 0) {
        useGameStore.getState().addPartyMember(createPartyMember("surggie_1", 5));
      }
      const partyState = useGameStore.getState();
      const activeMember =
        partyState.party[partyState.battle.activePartyIndex] ?? partyState.party[0];
      battleEngineRef.current = createBoundBattleEngine(
        participantFromMember(activeMember),
        buildParticipant(
          opponentSpeciesId,
          opponentLevel,
          opponentHpMax,
          opponentHpMax,
        ),
        { isWild },
      );
      // Maintain the v1 activeEncounter sentinel so the JSX path that depends
      // on it (BattleArena overlay) still renders. The fromWildSpriteId is
      // also forwarded back to the wild-sprites manager so it can be
      // despawned (it's already removed via consumeEncounter, but trainer/
      // legacy emitters won't have done that).
      if (fromWildSpriteId) {
        wildSpritesRef.current.removeSprite(fromWildSpriteId);
      }
      // Minimal WildSurggie-shaped stub for the legacy state container — the
      // BattleArena only reads opponent name + level + species and HP from
      // the battle store slice, not from this struct. Cast through unknown so
      // we don't pin the full WildSurggie shape here.
      setActiveEncounter({
        wildSurggie: {
          speciesId: Number.parseInt(opponentSpeciesId, 10) || 0,
          level: opponentLevel,
          max_hp: opponentHpMax,
          name: opponentSpeciesId,
        } as unknown as import("@/engine/encounter").WildSurggie,
        xpReward: Math.floor(20 + opponentLevel * 5),
      });
      globalEncounterSystem.startBattle();
      // Pre-battle BGM swap.
      preBattleBgmRef.current = currentBgmRef.current;
      const battleTrack: BgmKey = isWild ? "battle_wild" : "battle_trainer";
      playBgm(battleTrack);
      currentBgmRef.current = battleTrack;
    };
    window.addEventListener("encounter:start:v2", handleV2);
    return () => {
      window.removeEventListener("encounter:start:v2", handleV2);
    };
  }, [startBattle, setBackdrop]);

  // ── Handle window resize ────────────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas || !engineRef.current) return;

      // Fill the parent container
      const parent = canvas.parentElement;
      if (parent) {
        // devicePixelRatio-aware sizing: render at the device's physical pixel
        // density (capped at 2x to bound mobile memory) and let CSS scale the
        // canvas back down to its logical size. This keeps pixel art crisp on
        // retina / high-DPI phones. The renderer reads ctx.canvas.width/height
        // directly, so the camera viewport must match the backing-store size.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.max(1, Math.round(parent.clientWidth * dpr));
        canvas.height = Math.max(1, Math.round(parent.clientHeight * dpr));

        // Update camera viewport (backing-store pixels)
        engineRef.current.camera.setViewport(canvas.width, canvas.height);

        // Re-disable smoothing after resize
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.imageSmoothingEnabled = false;
        }
      }
    };

    window.addEventListener("resize", handleResize);
    // Initial sizing
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ── Map change subscription: BGM swap + wild-sprite/trainer config refresh ─
  useEffect(() => {
    return useGameStore.subscribe((state, prev) => {
      if (state.view === prev.view) return;

      // BGM mapping by view.
      let bgm: BgmKey;
      switch (state.view) {
        case "section":
          bgm = "route";
          break;
        case "department":
          bgm = "town";
          break;
        case "overworld":
        default:
          bgm = "overworld";
          break;
      }
      if (currentBgmRef.current !== bgm) {
        playBgm(bgm);
        currentBgmRef.current = bgm;
      }

      // Refresh wild-sprite zones + trainer placements when entering a known
      // static map. For procedural overworld we leave both empty so no wilds
      // spawn there.
      if (!engineRef.current) return;
      const mapId = engineRef.current.roomManager.getCurrentMapId();
      if (mapId === "route_1") {
        const spawnConfigs: ZoneSpawnConfig[] = ROUTE1_MAP.encounterZones.map((z) => ({
          zoneId: z.id,
          maxConcurrent: 3,
          spawnIntervalMs: 6000,
          speciesPool: z.speciesPool.map((p) => ({
            speciesId: p.speciesId,
            weight: p.weight,
            levelMin: p.levelMin,
            levelMax: p.levelMax,
          })),
          bounds: {
            minX: z.bounds.x,
            minY: z.bounds.y,
            maxX: z.bounds.x + z.bounds.w - 1,
            maxY: z.bounds.y + z.bounds.h - 1,
          },
        }));
        wildSpritesRef.current = new WildSpritesManager(spawnConfigs);
        const placements: DetectionPlacement[] = ROUTE1_MAP.trainers.map((t) => ({
          trainerId: t.trainerId,
          x: t.x,
          y: t.y,
          // TODO(integration): route1 trainers don't yet carry facing/
          // sightRange — default both until that map data ships.
          facing: "down",
          sightRange: 4,
        }));
        trainerDetectionRef.current.setActiveTrainers(placements);
      } else {
        wildSpritesRef.current = new WildSpritesManager([]);
        trainerDetectionRef.current.setActiveTrainers([]);
      }
    });
  }, []);

  // ── Achievement hooks driven by battle phase transitions ───────────────
  useEffect(() => {
    return useGameStore.subscribe((state, prev) => {
      // Catch outcome.
      if (prev.battle.phase !== "caught" && state.battle.phase === "caught") {
        if (state.battle.opponentSpeciesId) {
          achievementTracker.onCatch(state.battle.opponentSpeciesId);
        }
      }
      // Trainer victory.
      if (
        prev.battle.phase !== "victory" &&
        state.battle.phase === "victory" &&
        !state.battle.isWild
      ) {
        // TODO(integration): thread the actual defeated trainer id here once
        // battle slice tracks it. opponentSpeciesId is the closest proxy.
        achievementTracker.onTrainerDefeat(state.battle.opponentSpeciesId ?? "unknown");
      }
      // Level-up.
      if (state.playerLevel > prev.playerLevel) {
        achievementTracker.onLevelUp(state.playerLevel);
        playSfx("level_up");
      }
      // Battle ended — restore pre-battle BGM.
      if (prev.battle.active && !state.battle.active) {
        const restore = preBattleBgmRef.current ?? "overworld";
        playBgm(restore);
        currentBgmRef.current = restore;
        preBattleBgmRef.current = null;
      }
      // TODO(integration): wire achievementTracker.onEvolve() once the
      // evolution event surface is finalized.
    });
  }, []);

  // ── Post-battle party sync: HP write-back, XP/levels, evolution, moves ──
  useEffect(() => {
    return useGameStore.subscribe((state, prev) => {
      // Fires on every battle-end path (victory, defeat, caught, fled).
      if (!(prev.battle.active && !state.battle.active)) return;

      const engine = battleEngineRef.current;
      battleEngineRef.current = null;

      const store = useGameStore.getState();
      const outcome = state.battle.phase;
      const idx = prev.battle.activePartyIndex;
      const member = store.party[idx];

      if (member && engine) {
        // HP write-back from the engine's final player state.
        const hpCurrent = Math.max(
          0,
          Math.min(engine.player.hpCurrent, member.hpMax),
        );
        store.updatePartyMember(idx, { hpCurrent, fainted: hpCurrent <= 0 });

        if (outcome === "victory") {
          const gained = Math.max(8, prev.battle.opponentLevel * 12);
          const { member: after, levelsGained } = applyXp(
            { ...member, hpCurrent, fainted: hpCurrent <= 0 },
            gained,
          );
          store.updatePartyMember(idx, after);

          if (levelsGained > 0) {
            const species = resolveSpecies(after.speciesId);

            // Evolution check at the new level.
            const evo = checkEvolution(
              { speciesId: String(species?.id ?? after.speciesId), level: after.level },
              { kind: "level", level: after.level },
            );
            if (evo) store.pushEvolution(evo);

            // New moves for each level gained: learn into a free slot, or
            // queue a replace prompt when the moveset is full.
            const learnKey = species?.name ?? after.speciesId;
            const moves = [...after.moves];
            for (let lvl = after.level - levelsGained + 1; lvl <= after.level; lvl++) {
              for (const moveId of getMovesLearnedAtLevel(learnKey, lvl)) {
                if (moves.includes(moveId)) continue;
                if (moves.length < 4) {
                  moves.push(moveId);
                } else {
                  store.pushMoveLearn({ partyIndex: idx, moveId });
                }
              }
            }
            if (moves.length !== after.moves.length) {
              store.updatePartyMember(idx, {
                moves,
                pp: { ...after.pp, ...fullPp(moves.slice(after.moves.length)) },
              });
            }
          }
        }
      }

      // Successful capture: add the wild to the party.
      if (outcome === "caught" && prev.battle.opponentSpeciesId) {
        const added = store.addPartyMember(
          createPartyMember(prev.battle.opponentSpeciesId, prev.battle.opponentLevel),
        );
        if (!added) {
          store.appendBattleLog("Party full!");
        }
      }
    });
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className={className}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          imageRendering: "pixelated",
          backgroundColor: "#0A1628",
          touchAction: "none",
        }}
        tabIndex={0}
      />
      {activeEncounter && battle.active && (() => {
        const activeMember = party[battle.activePartyIndex] ?? party[0];
        const opponentSpeciesId =
          battle.opponentSpeciesId ?? String(activeEncounter.wildSurggie.speciesId);
        const battleStage = battle.phase;
        const canUseMenu = battleStage === "player_turn" || battleStage === "move_selection";

        const playerParticipant = activeMember
          ? participantFromMember(activeMember)
          : buildParticipant("default", 1, 20, 20, "Player");
        const opponentParticipant = buildParticipant(
          opponentSpeciesId,
          battle.opponentLevel,
          battle.opponentHpCurrent,
          battle.opponentHpMax,
          activeEncounter.wildSurggie.name,
        );

        const handleRun = () => {
          if (battleEngineRef.current) {
            void dispatchBattleAction(battleEngineRef.current, { kind: "flee" });
            battleEngineRef.current = null;
          } else {
            endBattle("fled");
          }
          globalEncounterSystem.attemptRun();
          globalEncounterSystem.resolve();
          encounterManagerRef.current.reset();
          setActiveEncounter(null);
        };

        const handleMoveSelected = (moveId: string) => {
          if (battleEngineRef.current) {
            void dispatchBattleAction(battleEngineRef.current, { kind: "move", moveId });
          }
        };
        const handleItemUsed = (itemId: string) => {
          if (battleEngineRef.current) {
            void dispatchBattleAction(battleEngineRef.current, { kind: "item", itemId });
          }
        };
        const handleSurggieSwitch = (partyIndex: number) => {
          const engine = battleEngineRef.current;
          if (engine) {
            const store = useGameStore.getState();
            const entry = store.party[partyIndex];
            if (entry && !entry.fainted && partyIndex !== store.battle.activePartyIndex) {
              // Write the outgoing member's battle HP back before swapping so
              // mid-battle damage isn't lost.
              const outIdx = store.battle.activePartyIndex;
              const outMember = store.party[outIdx];
              if (outMember) {
                const hp = Math.max(0, Math.min(engine.player.hpCurrent, outMember.hpMax));
                store.updatePartyMember(outIdx, { hpCurrent: hp, fainted: hp <= 0 });
              }
              store.switchActivePartyMember(partyIndex);
              dispatchBattleAction(engine, {
                kind: "switch",
                replacement: participantFromMember(entry),
              });
            }
          }
        };

        const battleMoveOptions: MoveInfo[] = playerParticipant.moves
          .slice(0, 4)
          .map((moveId) => {
            const move = MOVES_DATA.find(
              (m) => m.id === Number.parseInt(moveId, 10),
            );
            return {
              id: moveId,
              name: move?.name ?? moveId,
              type: move?.type ?? "normal",
              power: move?.power ?? 40,
              accuracy: move?.accuracy ?? 100,
              pp: playerParticipant.pp[moveId] ?? move?.pp ?? 0,
              ppMax: move?.pp ?? 0,
            };
          });
        const battleItems: ItemEntry[] = [
          { id: "ball-basic", name: "Catch Ball", description: "Catch a wild Surggie.", count: 3, category: "ball" },
          { id: "potion", name: "Potion", description: "Restore HP.", count: 2, category: "heal" },
          { id: "escape-orb", name: "Escape Orb", description: "Guaranteed flee.", count: 1, category: "flee" },
        ];
        const battleParty: PartyMember[] = party.slice(0, 6).map((member, i) => ({
          speciesId: member.speciesId,
          nickname: member.nickname ?? resolveSpecies(member.speciesId)?.name,
          level: member.level,
          hpCurrent: member.hpCurrent,
          hpMax: member.hpMax,
          fainted: member.fainted,
          active: i === battle.activePartyIndex,
        }));

        const handleMoveSelectUi = (moveId: string) => {
          handleMoveSelected(moveId);
        };

        const handleItemUseUi = (itemId: string) => {
          handleItemUsed(itemId);
        };

        const handlePartySwitchUi = (partyIndex: number) => {
          handleSurggieSwitch(partyIndex);
        };

        return (
          <div className="absolute inset-0">
            <BattleArena
              player={playerParticipant}
              opponent={opponentParticipant}
              party={battleParty}
              moves={battleMoveOptions}
              items={battleItems}
              log={battle.log}
              isWild={battle.isWild}
              backdropKey={battle.backdropKey}
              effects={[]}
              onMoveSelected={handleMoveSelectUi}
              onItemUsed={handleItemUseUi}
              onSurggieSwitch={handlePartySwitchUi}
              onRun={handleRun}
              menuEnabled={canUseMenu}
            />
            <div className="absolute inset-0 pointer-events-none">
              {(battle.phase === "caught" || battle.phase === "victory") && (
                <div className="absolute inset-x-4 top-4 flex justify-center pointer-events-auto">
                  <ShareButton
                    kind={battle.phase === "caught" ? "catch" : "victory"}
                    name={activeEncounter.wildSurggie.name}
                    level={battle.opponentLevel}
                  />
                </div>
              )}
              <div className="absolute inset-x-4 bottom-4 flex justify-end pointer-events-auto">
                <div className="flex-1 max-w-[520px]">
                  <BattleLog lines={battle.log} visibleLines={3} />
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {selectedSurggie && (
        <SurggieDialog surggie={selectedSurggie} onClose={closeSurggieDialog} />
      )}
      <KeyboardHintsOverlay />
    </div>
  );
}
